import {
  ThemeProvider,
  CssBaseline,
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import { pagesConfig } from "./pages.config";
import PageNotFound from "./lib/PageNotFound";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { NavigationProvider, useNavigation } from "./lib/NavigationContext";
import UserNotRegisteredError from "./components/UserNotRegisteredError";

const { Pages, Layout } = pagesConfig;

const Navbar = () => {
  const { currentPage, navigateTo } = useNavigation();

  const navItems = [
    { name: "Preventivi", key: "Quote" },
    { name: "Storico", key: "History" },
    { name: "Report", key: "Reports" },
    { name: "Admin Rates", key: "AdminRates" },
    { name: "Admin Conditions", key: "AdminConditions" },
  ];

  return (
    <Toolbar
      sx={{
        maxWidth: 1280,
        width: "100%",
        mx: "auto",
        px: { xs: 2, sm: 3 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexGrow: 1,
          cursor: "pointer",
        }}
        onClick={() => navigateTo("Quote")}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SecurityIcon sx={{ color: "primary.contrastText", fontSize: 22 }} />
        </Box>
        <Box>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
            Mobility Safe
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Piattaforma Preventivi
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          gap: 1,
        }}
      >
        {navItems.map((item) => (
          <Button
            key={item.key}
            onClick={() => navigateTo(item.key)}
            size="small"
            sx={{
              fontWeight: currentPage === item.key ? 700 : 500,
              color:
                currentPage === item.key ? "primary.main" : "text.secondary",
              px: 2,
              borderRadius: 2,
              transition: "all 0.2s",
              position: "relative",
              "&:hover": {
                color: "primary.main",
                bgcolor: "primary.lighter",
              },
              ...(currentPage === item.key && {
                bgcolor: "rgba(25, 118, 210, 0.08)", // primary.lighter equivalent
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: 0,
                  left: "20%",
                  right: "20%",
                  height: 3,
                  bgcolor: "primary.main",
                  borderRadius: "3px 3px 0 0",
                },
              }),
            }}
          >
            {item.name}
          </Button>
        ))}
      </Box>
    </Toolbar>
  );
};

const LayoutWrapper = ({ children, currentPageName }) =>
  Layout ? (
    <Layout currentPageName={currentPageName}>{children}</Layout>
  ) : (
    <>{children}</>
  );

function Navigation() {
  const { currentPage } = useNavigation();
  const Page = Pages[currentPage];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Navbar />
      <LayoutWrapper currentPageName={currentPage || "Not Found"}>
        {Page ? <Page /> : <PageNotFound />}
      </LayoutWrapper>
    </Box>
  );
}

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin } =
    useAuth();

  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (authError) {
    if (authError.type === "user_not_registered") {
      return <UserNotRegisteredError />;
    } else if (authError.type === "auth_required") {
      navigateToLogin();
      return null;
    }
  }

  return <Navigation />;
};

function MobilityQuote() {
  return (
    <AuthProvider>
      <NavigationProvider>
        <AuthenticatedApp />
      </NavigationProvider>
    </AuthProvider>
  );
}

export default MobilityQuote;
